// Device kit table row with calibration status and action buttons.

import type { JSX } from "react";

export type KitStatus = "available" | "assigned" | "maintenance" | "retired";

export interface DeviceKit {
  _id: string;
  kit_name: string;
  kit_type: string;
  serial_number: string;
  status: KitStatus;
  assigned_to_carer_id: string | null;
  assigned_to_patient_id: string | null;
  assigned_at: string | null;
  last_calibration_date: string | null;
  next_calibration_due: string | null;
  notes: string | null;
}

const STATUS_BADGE: Record<KitStatus, string> = {
  available:   "bg-green-100 text-green-700",
  assigned:    "bg-blue-100 text-blue-700",
  maintenance: "bg-amber-100 text-amber-700",
  retired:     "bg-slate-100 text-slate-500",
};

export function KitRow({ kit, onAssign, onReturn, onEditNotes }: {
  kit: DeviceKit;
  onAssign: (id: string) => void;
  onReturn: (id: string) => void;
  onEditNotes: (kit: DeviceKit) => void;
}): JSX.Element {
  const calDue = kit.next_calibration_due ? new Date(kit.next_calibration_due) : null;
  const calOverdue = calDue && calDue < new Date();
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-3 px-4 text-sm font-medium text-slate-900">{kit.kit_name}</td>
      <td className="py-3 px-4 text-sm text-slate-600">{kit.kit_type}</td>
      <td className="py-3 px-4 text-xs text-slate-500 font-mono">{kit.serial_number}</td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[kit.status]}`}>
          {kit.status}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-slate-500">
        {calDue ? (
          <span className={calOverdue ? "text-red-600 font-medium" : ""}>
            {calDue.toLocaleDateString("en-IN")}{calOverdue && " (overdue)"}
          </span>
        ) : "—"}
      </td>
      <td className="py-3 px-4 text-right space-x-2">
        {kit.status === "available" && (
          <button onClick={() => onAssign(kit._id)} className="text-xs text-navy-700 hover:text-navy-900 font-medium">
            Assign
          </button>
        )}
        {kit.status === "assigned" && (
          <button onClick={() => onReturn(kit._id)} className="text-xs text-amber-600 hover:text-amber-800 font-medium">
            Return
          </button>
        )}
        <button onClick={() => onEditNotes(kit)} className="text-xs text-slate-500 hover:text-slate-800 font-medium">
          Notes
        </button>
      </td>
    </tr>
  );
}
